import { z } from "zod";

// ── Entity schemas ──────────────────────────────────────────────────

export const EntitySummarySchema = z.object({
  id: z.string(),
  kind: z.string(),
  definitionId: z.string(),
  x: z.number(),
  y: z.number(),
  description: z.string().optional(),
  actions: z.array(z.string()).optional(),
  discovered: z.boolean().optional(),
});

export const PortalSummarySchema = z.object({
  id: z.string(),
  toScene: z.string(),
  x: z.number(),
  y: z.number(),
});

export const EntityDetailSchema = z.object({
  id: z.string(),
  kind: z.string(),
  definitionId: z.string(),
  position: z.object({ x: z.number(), y: z.number() }),
  facing: z.enum(["up", "down", "left", "right"]),
  visible: z.boolean(),
  description: z.string().optional(),
  actions: z.array(z.string()),
  components: z.array(z.string()),
});

// ── Response schemas ────────────────────────────────────────────────

export const SceneResponseSchema = z.object({
  sceneId: z.string(),
  title: z.string().optional(),
  size: z.object({ width: z.number(), height: z.number() }),
  player: z.object({
    x: z.number(),
    y: z.number(),
    facing: z.enum(["up", "down", "left", "right"]),
  }),
  entities: z.array(EntitySummarySchema),
  portals: z.array(PortalSummarySchema),
});

export const InventoryItemSchema = z.object({
  instanceId: z.string(),
  definitionId: z.string(),
  category: z.enum(["artifact", "tool", "clothing", "misc"]),
  quantity: z.number(),
  stackable: z.boolean(),
});

export const InventoryResponseSchema = z.object({
  items: z.array(InventoryItemSchema),
});

export const KnowledgeResponseSchema = z.object({
  progress: z.record(
    z.string(),
    z.enum(["discovered", "partiallyUnderstood", "understood"]),
  ),
});

export const NearbyResponseSchema = z.object({
  count: z.number(),
  entities: z.array(EntitySummarySchema),
});

// ── Action result schemas ───────────────────────────────────────────

export const MoveResultSchema = z.object({
  moved: z.literal(true),
  position: z.object({ x: z.number(), y: z.number() }),
});

export const TakeResultSchema = z.object({
  taken: z.string(),
});

export const DropResultSchema = z.object({
  dropped: z.string(),
  position: z.object({ x: z.number(), y: z.number() }),
});

export const InteractResultSchema = z.object({
  action: z.string(),
  entityId: z.string(),
  message: z.string().optional(),
});

export const RestartResultSchema = z.object({
  restarted: z.literal(true),
});

export const NavigateResultSchema = z.object({
  navigated: z.string(),
  player: z.object({
    x: z.number(),
    y: z.number(),
    facing: z.string(),
  }),
  entities: z.number(),
});

export const EquipResultSchema = z.object({
  equipped: z.string(),
});

export const UnequipResultSchema = z.object({
  unequipped: z.string(),
});

export const DiscoverResultSchema = z.object({
  discovered: z.string(),
});

export const ExamineResultSchema = z.object({
  examined: z.string(),
});

// ── JSON Schema converters ──────────────────────────────────────────

export function toJsonSchema<T extends z.ZodType>(schema: T): Record<string, unknown> {
  return z.toJSONSchema(schema) as Record<string, unknown>;
}
